// client/data-view-options.ts
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flat-loader.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { FieldConfiguration, makeUniqueIDForFieldConf } from "@adornis/field-management/field-configuration.js";
import {
  buildFullFieldManagementOptions
} from "@adornis/field-management/field-management-options.js";
import "@adornis/field-management/x-field-selection-drawer.js";
import { AdornisFilter } from "@adornis/filter/AdornisFilter.js";
import { subscribePaginatedAndFiltered } from "@adornis/filter/db/subscribePaginatedAndFiltered.js";
import { buildFullOptions as buildFullFilterOptions } from "@adornis/filter/filter-options.js";
import { getStandardFilterOperation } from "@adornis/filter/get-standard-filter-operation.js";
import { getBestEncodingForUserAgent } from "@adornis/import-export/encoding-helpers.js";
import merge from "deepmerge";
import { html, nothing } from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import { buildFullColumnDefinition } from "../ColumnDefinition.js";
import { convertColumnDefinitionsToSelectionSet } from "../convert-column-definitions.js";
import { View } from "../db/view.js";
var optionsFullyCalculated = Symbol("options fully calculated");
function getOperationFromOptions(fullOptions) {
  const operation = fullOptions.operation;
  if (typeof operation === "function")
    return operation;
  return getStandardFilterOperation(fullOptions.class, subscribePaginatedAndFiltered, operation);
}
function confColumnFromColDef(colDef) {
  return new FieldConfiguration({
    customID: colDef.customID,
    fieldName: colDef.fieldName,
    columnWidth: colDef.columnWidth
  });
}
function colDefFromFieldConf(fieldConf, existingColumns, options) {
  const found = existingColumns.find(
    (col) => col.fieldName === fieldConf.fieldName || col.customID === fieldConf.customID
  );
  return buildFullColumnDefinition(
    found ?? { fieldName: fieldConf.fieldName ?? void 0, customID: fieldConf.customID ?? void 0 },
    options,
    fieldConf
  );
}
function makeFinalColumns(options, view) {
  let enriched = view.configuredColumns?.map((cc) => colDefFromFieldConf(cc, options.customColumns ?? [], options)) ?? [];
  for (const visibleColumn of options.customColumns?.filter(
    (cc) => cc.isAlwaysVisible && !enriched.find((ec) => makeUniqueIDForFieldConf(cc) === makeUniqueIDForFieldConf(ec))
  ) ?? [])
    enriched = [buildFullColumnDefinition(visibleColumn, options), ...enriched];
  for (const staticCol of enriched.filter((ec) => !!ec.position)) {
    const without = enriched.filter((c) => c !== staticCol && !c.isAlwaysVisible);
    const alwaysVisibleFront = enriched.filter((c) => c !== staticCol && c.isAlwaysVisible && c.position === "front");
    const alwaysVisibleBack = enriched.filter((c) => c !== staticCol && c.isAlwaysVisible && c.position === "back");
    if (staticCol.position === "back")
      enriched = [...alwaysVisibleFront, ...without, staticCol, ...alwaysVisibleBack];
    else
      enriched = [...alwaysVisibleFront, staticCol, ...without, ...alwaysVisibleBack];
  }
  if (!enriched.length)
    enriched.push(buildFullColumnDefinition({ fieldName: "_id" }, options));
  const columnSelectorID = "columnSelector";
  if (options.isColumnSelectable && !enriched.find((ec) => ec.customID === columnSelectorID)) {
    const ds = DesignSystem.currentTheme.getValue();
    enriched.push(
      buildFullColumnDefinition(
        {
          customID: columnSelectorID,
          position: "back",
          isResizable: false,
          isSticky: true,
          isAlwaysVisible: true,
          renderHeader: () => {
            const drawer = createRef();
            return xComponents(html`
              <div
                ${css({ display: "contents" })}
                @click=${async () => {
              const newSelection = await drawer.value?.chooseFields({
                ...options.fieldSelectionOptions,
                selected: options.view?.configuredColumns ?? [],
                extraFields: options.customColumns?.filter((col) => !col.isAlwaysVisible && (!!col.customID || !!col.fieldName)).map((col) => new FieldConfiguration({ ...col, displayName: col.columnName }))
              });
              if (newSelection)
                view.configuredColumns = newSelection;
            }}
              >
                ${typeof options.isColumnSelectable !== "boolean" ? options.isColumnSelectable : html`
                      <x-icon
                        ${css({
              cursor: "pointer",
              background: ds.colors.accent,
              color: ds.colors.on.accent,
              fontSize: "30px"
            })}
                      >
                        add
                      </x-icon>
                    `}
              </div>

              <x-field-selection-drawer ${ref(drawer)}></x-field-selection-drawer>
            `);
          },
          columnWidth: "30px",
          renderItem: () => nothing
        },
        options
      )
    );
  }
  if (new Set(enriched.map((c) => c._id)).size !== enriched.length)
    throw new Error("customID are not unique: " + enriched.map((c) => makeUniqueIDForFieldConf(c)));
  return enriched;
}
function cleanUpViewColumnConfigurations(view, dataViewOptions) {
  if (!view.configuredColumns)
    view.configuredColumns = dataViewOptions.customColumns?.map((cc) => new FieldConfiguration(cc));
  const seen = /* @__PURE__ */ new Set();
  view.configuredColumns = view.configuredColumns?.filter((col) => {
    if (seen.has(makeUniqueIDForFieldConf(col)))
      return false;
    seen.add(makeUniqueIDForFieldConf(col));
    return true;
  });
  view.configuredColumns = view.configuredColumns?.filter(
    (confCol) => !!dataViewOptions.customColumns?.find((cc) => cc.customID === confCol.customID) || !!confCol.fieldName
  ) ?? [];
}
var buildFullOptions = (dataViewOptions, filterOptions) => {
  if (!dataViewOptions[optionsFullyCalculated]) {
    if (!dataViewOptions.class) {
      throw new Error("XDataViewOptions requires a class field");
    }
    if (dataViewOptions.isEntryClickable && dataViewOptions.rowHref) {
      throw new Error("isEntryClickable and rowHref are mutually exclusive in XDataViewOptions");
    }
  }
  const view = dataViewOptions.view ?? new View({});
  if (!view.filter)
    view.filter = new AdornisFilter({});
  cleanUpViewColumnConfigurations(view, dataViewOptions);
  const finalCombinedOptions = {
    dataViewOptions: dataViewOptions[optionsFullyCalculated] ? dataViewOptions : {
      ...dataViewOptions,
      loader: dataViewOptions.loader ?? (() => xComponents(html`
                <x-flex padding="sm" ${css({ gridColumn: "1 / -1" })} space="sm">
                  ${new Array(20).fill(null).map(
        (_, i) => html`
                          <x-flat-loader
                            ${css({ height: "30px", animationDelay: i / 20 * 0.5 + "s" })}
                          ></x-flat-loader>
                        `
      )}
                </x-flex>
              `)),
      emptyState: dataViewOptions.emptyState ?? (() => xComponents(
        html`
                  <x-flex padding="xs" center crossaxis-center ${css({ gridColumn: "1 / -1" })}>
                    <x-empty-placeholder no-wrapper .text=${"Keine Daten vorhanden"}> </x-empty-placeholder>
                  </x-flex>
                `
      )),
      class: dataViewOptions.class,
      finalColumns: () => makeFinalColumns(finalCombinedOptions.dataViewOptions, view),
      chooseEncoding: dataViewOptions.chooseEncoding ? () => dataViewOptions.chooseEncoding(getBestEncodingForUserAgent()) : async () => getBestEncodingForUserAgent(),
      operation: getOperationFromOptions(dataViewOptions),
      isSortable: dataViewOptions.isSortable ?? true,
      shouldRenderHeader: dataViewOptions.shouldRenderHeader ?? true,
      isColumnHeaderClickable: dataViewOptions.isColumnHeaderClickable ?? dataViewOptions.isSortable ?? true,
      isColumnResizable: dataViewOptions.isColumnResizable ?? false,
      isColumnResortable: dataViewOptions.isColumnResortable ?? false,
      isColumnSelectable: dataViewOptions.isColumnSelectable ?? false,
      showFullFieldPath: dataViewOptions.showFullFieldPath ?? false,
      customColumns: dataViewOptions.customColumns ?? [],
      isEntryClickable: dataViewOptions.isEntryClickable ?? true,
      rowHref: dataViewOptions.rowHref ?? (() => void 0),
      defaultFieldMapping: {
        Date: (v) => v.toLocaleString(),
        // TODO remove, this is legacy
        DateTime: (v) => v.toLocaleString(),
        boolean: (v) => html` <x-checkbox disabled .value=${v} ${css({ alignSelf: "center" })}></x-checkbox> `,
        number: (v) => String(v),
        ...dataViewOptions.defaultFieldMapping
      },
      finalSelectionSet: () => merge(
        dataViewOptions.selectionSet ?? {},
        convertColumnDefinitionsToSelectionSet(
          dataViewOptions.class,
          makeFinalColumns(finalCombinedOptions.dataViewOptions, view)
        )
      ),
      fieldSelectionOptions: buildFullFieldManagementOptions({
        whitelist: dataViewOptions.whitelistColumnSelectionFields,
        blacklist: dataViewOptions.blacklistColumnSelectionFields,
        ...dataViewOptions.fieldSelectionOptions,
        class: dataViewOptions.class
      }),
      view,
      onViewChanged: dataViewOptions.onViewChanged ?? (() => {
      }),
      entryClickHandler: dataViewOptions.entryClickHandler ?? (() => {
      }),
      renderGroupHeader: dataViewOptions.renderGroupHeader ?? (() => nothing),
      [optionsFullyCalculated]: true
    },
    filterOptions: buildFullFilterOptions({
      ...filterOptions,
      overridesForFieldName: {
        ...filterOptions?.overridesForFieldName,
        ...(dataViewOptions.customColumns ?? []).filter((c) => !!c.filterAspect && c.fieldName).reduce((all, c) => {
          if (Array.isArray(c.filterAspect)) {
            return {
              ...all,
              [c.fieldName]: c.filterAspect.map((fa) => ({
                ...fa,
                onlyForField: c.fieldName
              }))
            };
          }
          return {
            ...all,
            [c.fieldName]: {
              ...c.filterAspect,
              onlyForField: c.fieldName
            }
          };
        }, {})
      },
      overridesForIdentifier: {
        ...filterOptions?.overridesForIdentifier,
        ...(dataViewOptions.customColumns ?? []).filter((c) => !!c.filterAspect && c.customID).reduce((all, c) => {
          if (Array.isArray(c.filterAspect)) {
            return {
              ...all,
              [c.customID]: c.filterAspect.map((fa) => ({
                ...fa,
                onlyForIdentifier: c.customID
              }))
            };
          }
          return {
            ...all,
            [c.customID]: {
              ...c.filterAspect,
              onlyForIdentifier: c.customID
            }
          };
        }, {})
      },
      // TODO maybe automatically use dv field management options
      // TODO if you implement this, take care of passing by reference. To test, open field selection and then filter and the other way around and verify contents
      fieldAspectGeneration: {
        ...filterOptions?.fieldAspectGeneration,
        class: dataViewOptions.class
      },
      filter: view.filter
    })
  };
  return finalCombinedOptions;
};
export {
  buildFullOptions,
  colDefFromFieldConf,
  confColumnFromColDef,
  getOperationFromOptions,
  makeFinalColumns,
  optionsFullyCalculated
};
